import { observer } from "mobx-react";
import ProductCaseStore from "../../../stores/product-case-store";
import { INCENTIVE_VIRTUAL_CURRENCIES } from "../../../../../../models/User";
import TableRow from "../../../../../../components/table/components/table-row/table-row";
import { Checkbox } from "../../../../../../components/inputs/checkbox/checkbox";
import { CHECKBOX_SIZES } from "../../../../../../components/inputs/tristate-checkbox/tristate-checkbox";
import { TOOLTIP_VARIANTS, Tooltip } from "../../../../../../components/tooltip/tooltip";
import FontBody2 from "../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../components/fonts/font-props";
import { TOOLTIP_PLACEMENTS } from "../../../../../../components/tooltip/tooltip-box";
import Icon from "../../../../../../components/icons/icon/icon";
import IconReturnNotRefundExchange from "../../../../../../components/icons/icon-return-not-refund-exchange/icon-return-not-refund-exchange";
import IconContactCustomerSupport from "../../../../../../components/icons/icon-contact-customer-support/icon-contact-customer-support";
import IconSwitchToExchangeTransparent from '../../../../../../components/icons/icon-switch-to-exchange-transparent/icon-switch-to-exchange-transparent';
import { ICON_SIZE } from "../../../../../../components/icons/icon-props-interface";
import MediaWithPlaceholder, { MEDIA_VARIANTS } from "../../../../../../components/media-with-placeholder/media-with-placeholder";
import TextTruncate from "../../../../../../components/text-truncate/text-truncate";
import Currency from "../../../../../../components/currency/currency";
import RmaItemBalance from "../rma-items-blalance/rma-items-blalance";
import RmaProductIndicationsIcons from "../../rma-product-indications-icons/rma-product-indications-icons";
import { ButtonIcon } from "../../../../../../components/inputs/button-icon/button-icon";
import IconCircleRightOutlined from "../../../../../../components/icons/icon-circle-right-outlined/icon-circle-right-outlined";
import IconWarning from "../../../../../../components/icons/icon-warning/icon-warning";
import FontBody3 from "../../../../../../components/fonts/font-body3/font-body3";
import IconRefunded from "../../../../../../components/icons/icon-refunded/icon-refunded";
import rmaPageMainStore from "../../../stores/rma-page-main-store";
import rmsMixpanelStore from "../../../../../../stores/rms-mixpanel-store";
import useDashboardTranslation from "../../../../../../dashboard-translation/hooks/dashboard-translation-hook";

interface IProps {
    productCaseList: ProductCaseStore[];
    isActionable: boolean;
    isOutOfPolicyItem: boolean;
}

const RmaItemsTableRowList: React.FC<IProps> = observer(({ productCaseList, isActionable, isOutOfPolicyItem }) => {
    const scope = 'dashboard-returns-page';
    const {getTranslation} = useDashboardTranslation();

    const toggleProductCase = (productCase: ProductCaseStore) => {

        const rmaPageStore = rmaPageMainStore.getRmaPageStore()!;
        const rejectItemAction = rmaPageStore.rejectItemAction()!;
        const rejectItemActionStore = rejectItemAction.store;

        if(productCase.isChecked && rejectItemActionStore.shouldShowRejectPopup(productCase)) {
            rmaPageStore.rmaActionsToolbarStore().setCurrentAction(rejectItemAction);
            rejectItemActionStore.setProductCaseForReject(productCase);
            return;
        }

        productCase.setIsChecked(!productCase.isChecked);
        rmsMixpanelStore.trackEvent('d&_original_item_checkbox');
    }

    return (
        <>
            {productCaseList.map((productCase: ProductCaseStore) => {
                const productStore = productCase.productStore;
                const exchangeStore = productCase.exchangeStore;
                const returnReasonStore = productCase.returnReasonStore;
                const policyRuleStore = productCase.rmaOfferStore.policyRuleStore;
                const isProductCaseChecked = productCase.isChecked;

                const ManualExchangeCell = () => (
                    <Tooltip
                        content={
                            <div className={"rma-page-items-manual-exchange-request-details-tooltip"}>
                                <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE} variant={FONT_VARIANT.BOLD}>{productCase.getManualExchangeMessage()}</FontBody2>
                                {productCase.hasUploadedMediaFiles() && <FontBody3 color={FONT_COLOR.NEUTRAL_WHITE}>{"For more information, view the item's details"}</FontBody3>}
                            </div>
                        }
                        placement={TOOLTIP_PLACEMENTS.TOP}
                        variant={TOOLTIP_VARIANTS.INFO}
                    >
                        <TextTruncate>
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{productCase.getManualExchangeMessage()}</FontBody2>
                        </TextTruncate>
                    </Tooltip>
                );

                const NotExchangeCell = () => (
                    <Tooltip
                        content={
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{"Not applicable"}</FontBody2>
                        }
                        placement={TOOLTIP_PLACEMENTS.TOP}
                        variant={TOOLTIP_VARIANTS.INFO}
                    >
                        <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{"N/A"}</FontBody2>
                    </Tooltip>
                );

                const RegularExchangeCell = () => {
                    if (exchangeStore) {
                        return <div className={'original-item-cell'}>
                            {exchangeStore.image && <MediaWithPlaceholder variant={MEDIA_VARIANTS.XS} alt={exchangeStore.name} enlargeSrc={exchangeStore.image} src={exchangeStore.image} />}
                            <div className={"original-item-product-title"}>
                                <TextTruncate>
                                    <FontBody2 color={FONT_COLOR.PRIMARY2}>{exchangeStore.quantity + 'x '}</FontBody2>
                                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{exchangeStore.name}</FontBody2>
                                </TextTruncate>
                                <TextTruncate>
                                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>{exchangeStore.variantTitle}</FontBody2>
                                </TextTruncate>
                                <TextTruncate>
                                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>{exchangeStore.sku}</FontBody2>
                                </TextTruncate>
                            </div>
                        </div>
                    } else {
                        return <></>;
                    }
                };

                const SwitchToExchangeIcon = () => {
                    return (
                        <Tooltip
                            placement={TOOLTIP_PLACEMENTS.TOP}
                            variant={TOOLTIP_VARIANTS.INFO}
                            content={
                                <>
                                    {!!productCase.rmaOfferStore.switchToExchangeIncentiveAmount && (<div>
                                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                            {"Incentive given:"}
                                            {" "}
                                            <Currency
                                                currencyCode={productCase.itemsBalanceStore.originalItemPaidPrice?.currency_code}
                                                amount={productCase.rmaOfferStore.switchToExchangeIncentiveAmount}
                                            />
                                        </FontBody2>
                                    </div>)}
                                    {!!productCase.rmaOfferStore.switchToExchangeIncentiveVirtualCurrency && (<div>
                                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                            {"Incentive type:"}
                                            {" "}
                                            {productCase.rmaOfferStore.switchToExchangeIncentiveVirtualCurrency === INCENTIVE_VIRTUAL_CURRENCIES.STORE_CREDITS && "Store credits"}
                                            {productCase.rmaOfferStore.switchToExchangeIncentiveVirtualCurrency === INCENTIVE_VIRTUAL_CURRENCIES.GIFT_CARD && "Gift card"}
                                        </FontBody2>
                                    </div>)}
                                </>
                            }
                        >
                            <Icon Component={IconSwitchToExchangeTransparent} size={ICON_SIZE.SMALL} />
                        </Tooltip>
                    );
                }

                const ExchangeIcon = () => {
                    if (!policyRuleStore.isExchange) {
                        return null;
                    }

                    return policyRuleStore.isManualExchange
                            ? <Icon Component={IconContactCustomerSupport} size={ICON_SIZE.SMALL} />
                            : <Icon Component={IconReturnNotRefundExchange} size={ICON_SIZE.SMALL} />
                }

                const onItemSelect = () => {
                    rmsMixpanelStore.trackEvent('d&_original_item_more');
                    productCase.customerCaseStore.setSelectedProductCase(productCase);
                }
                
                const SelectItemCell: (productCase: ProductCaseStore) => (JSX.Element) = (productCase: ProductCaseStore) => {
                    if (isActionable && !productCase.isRejected) {
                        return <Checkbox
                            label={""}
                            checked={isProductCaseChecked}
                            onChange={() => toggleProductCase(productCase)}
                            size={CHECKBOX_SIZES.SMALL}
                        />
                    }
                    
                    if(isActionable && productCase.isRejected) {
                        return <div style={{pointerEvents: 'all', cursor: 'default'}}>
                            <Tooltip
                                placement={TOOLTIP_PLACEMENTS.RIGHT}
                                content={
                                    <>
                                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{getTranslation('REJECT_ITEM_LIST_CHECKBOX_TOOLTIP_ITEM_HAS_BEEN_REJECTED_TEXT', scope)}</FontBody2>
                                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE} variant={FONT_VARIANT.BOLD}>
                                            {getTranslation('REJECT_ITEM_LIST_CHECKBOX_TOOLTIP_REASON', scope)}&nbsp;
                                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                                {productCase.rejectedReason ?? '-'}
                                            </FontBody2>
                                        </FontBody2>
                                    </>
                                }
                            >
                                <Checkbox
                                    label={""}
                                    checked={false}
                                    size={CHECKBOX_SIZES.SMALL}
                                />
                            </Tooltip>
                        </div>
                    }

                    return <></>;
                }

                return <TableRow
                    onSelect={() => isActionable && productCase.customerCaseStore.setSelectedProductCase(productCase)}
                    key={productCase.id}
                    disabled={productCase.isRejected}
                    cells={[
                        SelectItemCell(productCase),
                        <div className={'original-item-cell'}>
                            <MediaWithPlaceholder variant={MEDIA_VARIANTS.XS} alt={productStore.name} enlargeSrc={productStore.imageUrl} src={productStore.imageUrl} />
                            <div className={"original-item-product-title"}>
                                <TextTruncate>
                                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{productStore.name}</FontBody2>
                                </TextTruncate>
                                <TextTruncate>
                                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>{productStore.variantTitle}</FontBody2>
                                </TextTruncate>
                                <TextTruncate>
                                    <FontBody2 color={FONT_COLOR.NEUTRAL3}>{productStore.sku}</FontBody2>
                                </TextTruncate>
                            </div>
                        </div>,
                        <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                            {productCase.itemsBalanceStore.originalItemPaidPrice && (
                                <Currency
                                    currencyCode={productCase.itemsBalanceStore.originalItemPaidPrice.currency_code}
                                    amount={+productCase.paidPrice}
                                />
                            )}
                        </FontBody2>,
                        <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{returnReasonStore.name}</FontBody2>,
                        <div className={'policy-rule-cell'}>
                            {productCase.isHiddenResolution && <IconWarning color="var(--color-accent-4)" />}
                            {!isOutOfPolicyItem && (
                                productCase.isSwitchToExchange
                                    ? <SwitchToExchangeIcon />
                                    : <ExchangeIcon />
                            )}
                            {!isOutOfPolicyItem && policyRuleStore.isReturn && <Icon Component={IconRefunded} size={ICON_SIZE.SMALL} />}
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                <TextTruncate>{policyRuleStore.displayName}</TextTruncate>
                            </FontBody2>
                        </div>,
                        <div>
                            {policyRuleStore.isManualExchange
                                ? <ManualExchangeCell />
                                : !policyRuleStore.isExchange && !isOutOfPolicyItem
                                    ? <NotExchangeCell />
                                    : <RegularExchangeCell />
                            }
                        </div>,
                        <RmaItemBalance
                            isHiddenResolution={productCase.isHiddenResolution}
                            itemsBalanceStore={productCase.itemsBalanceStore}
                        />,
                        <RmaProductIndicationsIcons productCaseStore={productCase} customerCaseStore={productCase.customerCaseStore} />,
                        isActionable && (
                            <Tooltip content={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>Open</FontBody2>}>
                                <ButtonIcon Component={IconCircleRightOutlined} onClick={() => onItemSelect()} />
                            </Tooltip>
                        ),
                    ]} />
            })}
        </>
    );
});

export default RmaItemsTableRowList;